<template>
  <div class="page-wrap">
    <div class="page-header">
      <h2><span class="breadcrumb-level">系统管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">系统配置</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">金刚区图标管理</span></h2>
    </div>
    <div class="home-icons-layout">
      <!-- 左侧：图标列表 -->
      <div class="left-panel">
        <div class="toolbar">
          <el-button type="primary" size="small" @click="showAddDialog">+ 新增图标</el-button>
        </div>
        <el-table :data="icons" stripe v-loading="loading" size="small" highlight-current-row @row-click="handlePreview">
          <el-table-column label="图标" width="50">
            <template #default="{ row }">
              <el-image :src="row.icon_url" style="width:28px;height:28px" fit="contain" />
            </template>
          </el-table-column>
          <el-table-column label="标题" prop="title" width="100" />
          <el-table-column label="排序" width="60">
            <template #default="{ row }">
              <el-input-number v-model="row.sort_order" :min="0" size="small" controls-position="right" @change="handleSortChange(row)" style="width:70px" />
            </template>
          </el-table-column>
          <el-table-column label="状态" width="60">
            <template #default="{ row }">
              <el-switch v-model="row.status" size="small" @change="handleStatusChange(row)" />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80">
            <template #default="{ row }">
              <el-button size="small" text @click="showEditDialog(row)">编辑</el-button>
              <el-popconfirm title="确定删除？" @confirm="handleDelete(row)">
                <template #reference>
                  <el-button size="small" text type="danger">删除</el-button>
                </template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 右侧：手机预览 -->
      <div class="right-panel">
        <div class="phone-wrapper">
          <div class="phone-frame">
            <div class="phone-notch"></div>
            <div class="phone-statusbar">
              <span>9:41</span>
              <div class="status-icons">
                <span class="signal"></span>
                <span class="wifi"></span>
                <span class="battery"></span>
              </div>
            </div>
            <div class="phone-content">
              <div class="homepage-preview">
                <!-- 轮播图占位 -->
                <div class="preview-banner">
                  <div class="banner-placeholder">轮播图</div>
                </div>
                <!-- 金刚区图标网格 -->
                <div class="preview-icons">
                  <div
                    v-for="icon in visibleIcons"
                    :key="icon.id"
                    class="preview-icon-item"
                  >
                    <div class="preview-icon-img">
                      <el-image :src="icon.icon_url" fit="contain" v-if="icon.icon_url" />
                      <span v-else class="preview-icon-empty">?</span>
                    </div>
                    <div class="preview-icon-label">{{ icon.title }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="phone-home-indicator"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑/新增对话框 -->
    <el-dialog v-model="dialogVisible" :title="isEdit ? '编辑图标' : '新增图标'" width="500px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="图标下方显示的文字" />
        </el-form-item>
        <el-form-item label="图标" prop="icon_url">
          <Upload v-model="form.icon_url" :max-count="1" :width="80" :height="80" fit="contain" tip="推荐尺寸 宽 52 × 高 52 px" />
        </el-form-item>
        <el-form-item label="跳转路径" prop="path">
          <el-input v-model="form.path" placeholder="小程序页面路径，如 /pages/index/index" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort_order" :min="0" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.status" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import * as contentApi from '../../api/contentManagement';
import Upload from '../../components/Upload.vue';

const icons = ref([]);
const loading = ref(false);
const saving = ref(false);
const dialogVisible = ref(false);
const isEdit = ref(false);
const formRef = ref(null);

const form = ref({
  id: null,
  title: '',
  icon_url: '',
  path: '',
  sort_order: 0,
  status: true,
});

const rules = {
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  icon_url: [{ required: true, message: '请上传图标', trigger: 'change' }],
};

const visibleIcons = computed(() => icons.value.filter(i => i.status !== false).slice(0, 10));

const fetchData = async () => {
  loading.value = true;
  try {
    const res = await contentApi.getHomeIcons();
    icons.value = (res.data || []).sort((a, b) => a.sort_order - b.sort_order);
  } catch { ElMessage.error('获取图标失败'); }
  finally { loading.value = false; }
};

const handleSortChange = async (row) => {
  try {
    await contentApi.updateHomeIcon(row.id, { sort_order: row.sort_order });
    await fetchData();
  } catch { ElMessage.error('更新排序失败'); }
};

const handleStatusChange = async (row) => {
  try {
    await contentApi.updateHomeIcon(row.id, { status: row.status });
  } catch { ElMessage.error('更新状态失败'); }
};

const showAddDialog = () => {
  isEdit.value = false;
  form.value = { id: null, title: '', icon_url: '', path: '', sort_order: 0, status: true };
  dialogVisible.value = true;
};

const showEditDialog = (row) => {
  isEdit.value = true;
  form.value = { ...row };
  dialogVisible.value = true;
};

const handlePreview = (row) => {
  // hover-driven, no action needed
};

const handleSave = async () => {
  const valid = await formRef.value.validate().catch(() => false);
  if (!valid) return;
  saving.value = true;
  try {
    if (isEdit.value) {
      await contentApi.updateHomeIcon(form.value.id, form.value);
      ElMessage.success('更新成功');
    } else {
      await contentApi.createHomeIcon(form.value);
      ElMessage.success('创建成功');
    }
    dialogVisible.value = false;
    await fetchData();
  } catch { ElMessage.error('保存失败'); }
  finally { saving.value = false; }
};

const handleDelete = async (row) => {
  try {
    await contentApi.deleteHomeIcon(row.id);
    ElMessage.success('删除成功');
    await fetchData();
  } catch { ElMessage.error('删除失败'); }
};

onMounted(fetchData);
</script>

<style scoped>
.page-wrap {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.home-icons-layout {
  display: flex;
  gap: 24px;
  flex: 1;
  min-height: 0;
}

/* 左侧面板 */
.left-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.toolbar {
  margin-bottom: 12px;
}

/* 右侧手机预览 */
.right-panel {
  width: 340px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 10px;
}
.phone-wrapper {
  position: sticky;
  top: 10px;
}
.phone-frame {
  width: 300px;
  background: #fff;
  border-radius: 32px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  padding: 12px;
  border: 2px solid #e0e0e0;
  overflow: hidden;
}
.phone-notch {
  width: 120px;
  height: 24px;
  background: #000;
  border-radius: 0 0 12px 12px;
  margin: -12px auto 4px;
}
.phone-statusbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  font-size: 11px;
  color: #333;
  font-weight: 600;
}
.status-icons {
  display: flex;
  align-items: center;
  gap: 3px;
}
.signal, .wifi, .battery {
  display: inline-block;
  width: 14px;
  height: 10px;
  background: currentColor;
  border-radius: 1px;
  opacity: 0.6;
}
.battery {
  width: 20px;
  height: 10px;
  border: 1px solid currentColor;
  background: transparent;
  position: relative;
}
.battery::after {
  content: '';
  position: absolute;
  right: -3px;
  top: 2px;
  width: 2px;
  height: 4px;
  background: currentColor;
  border-radius: 0 1px 1px 0;
}
.phone-content {
  min-height: 480px;
  background: #f8f8f8;
  border-radius: 8px;
  overflow-y: auto;
  margin: 4px 0;
}

/* 首页预览 */
.homepage-preview {
  padding: 0;
}
.preview-banner {
  height: 120px;
  background: linear-gradient(135deg, #e8f0fe, #f0f4ff);
  display: flex;
  align-items: center;
  justify-content: center;
}
.banner-placeholder {
  color: #999;
  font-size: 13px;
}
.preview-icons {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  padding: 12px 8px;
  background: #fff;
}
.preview-icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 4px;
}
.preview-icon-img {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 4px;
}
.preview-icon-img .el-image {
  width: 36px;
  height: 36px;
}
.preview-icon-empty {
  font-size: 14px;
  color: #ccc;
}
.preview-icon-label {
  font-size: 10px;
  color: #333;
  text-align: center;
  line-height: 1.2;
}
.phone-home-indicator {
  width: 120px;
  height: 4px;
  background: #ddd;
  border-radius: 2px;
  margin: 8px auto 2px;
}
</style>
